import type { Dispatch, MutableRefObject, ReactElement, SetStateAction } from "react"
import { Fragment, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useIsMutating, useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import { Box, CircularProgress } from "@mui/material"
import { Button, NotificationUtils, Skeleton, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { capitalize } from "lodash"
import { v4 as uuidv4 } from "uuid"

import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateWorkflowRequest } from "../../../types/custom/projects"
import type { CloneDataFilesRequest } from "../../../types/custom/rules"
import type { DataFileCloneResponse } from "../../../types/generated/api/DataFileCloneResponse"
import type { Label } from "../../../types/generated/api/Label"
import { ScoreCardCreateUpdateRequest } from "../../../types/generated/api/ScoreCardCreateUpdateRequest"
import { WorkflowCreateRequest } from "../../../types/generated/api/WorkflowCreateRequest"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { formatWorkflowComponent } from "../../../utils/WorkflowBlocksHelpers"
import { isRulesetFormikCard } from "../../Ruleset"
import type { RuleCardFormikValues } from "../../Ruleset"
import { RulesetCard } from "../../Ruleset/components/RulesetCard"
import { ScoreTable } from "../../ScoreTables"
import { formatCriteria, isTableFormatFormikValues } from "../../ScoreTables/helpers"
import type { ScoreTableFormik } from "../../ScoreTables/interfaces"
import type { ScoreCardFormikValues } from "../../Scorecards/Interfaces"
import { Scorecard } from "../../Scorecards/components/Scorecard"
import { TaglistCard } from "../../Taglist/components/TaglistCard"
import type { TagCardFormikValues } from "../../Taglist/interface"
import { CREATE_MUTATION_FN_MAPPER, VALIDATION_SCHEMAS_MAPPER } from "../Constants"
import { isNotScorecardCompName, updateComponentsDataFilesIds } from "../Helpers"
import type { AuditProps, ComponentTypeExtractor, Variant } from "../Interfaces"

import styles from "./Audit.module.scss"

/**
 * Maps a given variant to its corresponding component, providing props for rendering.
 *
 * @template TVariant - The variant type extending the Variant interface.
 * @param {Variant} variant - The type of component to render (e.g., Taglist, Ruleset, Scorecard).
 * @param {Object} props - Properties required to render the component.
 * @param {string} props.key - Unique key for the component.
 * @param {"import"} props.variant - Specifies the import variant.
 * @param {boolean} props.isCopying - Indicates if the component is in a copying state.
 * @param {string} props.uuid - Unique identifier for the component.
 * @param {Function} props.removeComponent - Function to remove the component by ID.
 * @param {Dispatch<SetStateAction<number>>} props.setTotalNumOfCurrentProjComponents - Function to set total number of current project components.
 * @param {Dispatch<SetStateAction<number>>} props.setCompletedCardsCounter - Function to set the counter for completed cards.
 * @param {boolean} props.isPermittedToCloneFiles - Indicates if file cloning is permitted.
 * @param {string} props.createdBy - Creator of the component.
 * @param {string} props.createdAt - Timestamp of when the component was created.
 * @param {MutableRefObject} props.allComponentsDataRef - Mutable ref object holding the formik values for the components.
 * @returns {ReactElement} - The corresponding React component for the given variant.
 */

const componentMapper = <TVariant extends Variant>(
  variant: Variant,
  props: {
    key: string
    variant: "import"
    isAllComponentsLoading: boolean
    uuid: string
    removeComponent: (id: string) => void
    setTotalNumOfCurrentProjComponents: Dispatch<SetStateAction<number>>
    setCompletedCardsCounter: Dispatch<SetStateAction<number>>
    setComponentsErrorMsgs: Dispatch<
      SetStateAction<
        | {
            [id: string]: string
          }
        | undefined
      >
    >
    isPermittedToCloneFiles: boolean
    canShowDetails: boolean
    createdBy: string
    createdAt: string
    allComponentsDataRef: MutableRefObject<{ [compId: string]: ComponentTypeExtractor<TVariant, "formikValues"> }>
    clonedFilesIdsMap: Record<string, string>
  },
): ReactElement => {
  const { key, allComponentsDataRef, ...rest } = props
  const mapper = {
    Taglist: (
      <TaglistCard
        key={key}
        allComponentsDataRef={allComponentsDataRef as MutableRefObject<{ [compId: string]: TagCardFormikValues }>}
        {...rest}
      />
    ),
    Ruleset: (
      <RulesetCard
        key={key}
        allComponentsDataRef={allComponentsDataRef as MutableRefObject<{ [compId: string]: RuleCardFormikValues }>}
        {...rest}
      />
    ),
    Scorecard:
      "format" in (allComponentsDataRef.current[rest.uuid] as ScoreTableFormik | ScoreCardFormikValues) &&
      (allComponentsDataRef.current[rest.uuid] as ScoreTableFormik).format === "table" ? (
        <ScoreTable
          key={key}
          allComponentsDataRef={allComponentsDataRef as MutableRefObject<{ [compId: string]: ScoreTableFormik }>}
          {...rest}
        />
      ) : (
        <Scorecard
          key={key}
          allComponentsDataRef={allComponentsDataRef as MutableRefObject<{ [compId: string]: ScoreCardFormikValues }>}
          {...rest}
        />
      ),
  }

  return mapper[variant]
}

/**
 * This component is used to edit a single component inside a project.
 *
 * @param componentName The name of the component to edit.
 * @param currentProjectStep The current step of the project.
 * @param numOfTotalSelectedProjects The total number of projects selected to import.
 * @param numOfCurrentProjComponents The number of components in the current project.
 * @param currentEditProjectData The data of the current project being edited.
 * @param componentsDataResults The data of the components in the project.
 * @param removeComponent A function to remove a component from the project.
 * @param incrementProjectStep A function to increment the project step.
 * @param allComponentsDataRef A reference to the data of all components in the project.
 *
 * @returns A JSX element containing the component to edit.
 */
export function Audit<TVariant extends Variant>(props: Readonly<AuditProps<TVariant>>): ReactElement {
  const {
    componentName,
    currentProjectStep,
    numOfCurrentProjComponents,
    numOfTotalSelectedProjects,
    currentEditProjectData,
    componentsDataResults,
    removeComponent,
    incrementProjectStep,
    allComponentsDataRef,
  } = props

  //#region Hooks
  const { id: projectId } = useParams<{ id: string }>()

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const canShowDetails = useIsPermitted(
    `Retrieve ${componentName.toLowerCase()}`,
    permissions.konan,
    flattenedKonanPermissions,
  )

  const clonedFilesIdsMapper = useRef<Record<string, string>>({})

  const { t } = useTranslation("common")

  // this state is needed to keep the total number of current project components persistent even after the user save
  // one component and it is removed from the list and decrement it when the user dismiss one
  const [totalNumOfCurrentProjComponents, setTotalNumOfCurrentProjComponents] = useState(numOfCurrentProjComponents)

  const [completedCardsCounter, setCompletedCardsCounter] = useState(0)

  const [componentsErrorMsgs, setComponentsErrorMsgs] = useState<{ [id: string]: string }>()

  // these `useIsMutating` hooks to check if any mutation inside any child component is loading to disable the header buttons
  const isWorkFlowMutating = useIsMutating({
    predicate: (mutation) => !!mutation.options.mutationKey?.includes("workFlowCreate"),
  })

  const isLabelsMutating = useIsMutating({
    predicate: (mutation) => !!mutation.options.mutationKey?.includes("labelsCreate"),
  })

  const isCloneMutating = useIsMutating({
    predicate: (mutation) => !!mutation.options.mutationKey?.includes("filesClone"),
  })

  const isCreateMutating = useIsMutating({
    predicate: (mutation) => !!mutation.options.mutationKey?.includes("componentCreate"),
  })

  //#endregion

  //#region Mutations
  // clone data-files mutation
  const cloneDataFilesMutation = useMutation<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>(
    KonanAPI.cloneDataFiles,
    {
      onSuccess({ data }) {
        data.forEach((file) => (clonedFilesIdsMapper.current[file.source.uuid] = file.uuid))
      },
    },
  )

  // create workflow mutation
  const createWorkflowMutation = useMutation<AxiosResponse, AxiosError, CreateWorkflowRequest>(
    KonanAPI.createWorkflow,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["workflows", projectId])
        await queryClient.invalidateQueries(["project", projectId])
      },
    },
  )

  // create Component
  const createComponentMutation = useMutation<
    AxiosResponse,
    AxiosError<{ name: string[] }>,
    ComponentTypeExtractor<TVariant, "createCompRequest">
  >(
    CREATE_MUTATION_FN_MAPPER[componentName] as (
      props: ComponentTypeExtractor<TVariant, "createCompRequest">,
    ) => Promise<AxiosResponse>,
  )

  // Create new label if it doesn't already exist
  const CreateLabelsMutation = useMutation<
    AxiosResponse<Label[]>,
    AxiosError,
    {
      project_uuid: string
      labelsNames: { name: string }[]
    }
  >(KonanAPI.BulkCreateLabels, {
    onSuccess: ({ data }) => {
      // update the components cards with the newly cloned labels
      Object.values(allComponentsDataRef.current).forEach((component) => {
        if (!isTableFormatFormikValues(component)) {
          component.cards.forEach((card) => {
            if (isRulesetFormikCard(card)) {
              const label = data.find((label) => label.name.toLowerCase() === card.label.toLowerCase())

              if (label) {
                card.label = label.name
                card.label_id = label.uuid
              }
            }
          })
        }
      })
    },
    onError: () => {
      NotificationUtils.toast(t("copyComponentFlow.audit.createLabelError"), { severity: "error" })
    },
  })
  //#endregion

  //#region Handlers
  const saveAllHandler = async (): Promise<void> => {
    setComponentsErrorMsgs(undefined)

    if (!projectId) return

    const validatedComponents = []

    // Validate all components data before sending any requests
    for (const component of componentsDataResults) {
      if (component.status !== "success") continue

      try {
        if (isNotScorecardCompName(componentName)) {
          VALIDATION_SCHEMAS_MAPPER[componentName].validateSync(allComponentsDataRef.current[component.data.data.uuid])

          validatedComponents.push(allComponentsDataRef.current[component.data.data.uuid])
        } else {
          const assertedComponentName = componentName as Extract<TVariant, "Scorecard">

          const componentFormikValues = allComponentsDataRef.current[component.data.data.uuid]

          if (componentFormikValues && isTableFormatFormikValues(componentFormikValues)) {
            VALIDATION_SCHEMAS_MAPPER[assertedComponentName]["Scoretable"].validateSync(
              allComponentsDataRef.current[component.data.data.uuid],
            )

            validatedComponents.push(allComponentsDataRef.current[component.data.data.uuid])
          } else {
            VALIDATION_SCHEMAS_MAPPER[assertedComponentName]["Scorecard"].validateSync(
              allComponentsDataRef.current[component.data.data.uuid],
            )

            validatedComponents.push(allComponentsDataRef.current[component.data.data.uuid])
          }
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMsg = err.message
          setComponentsErrorMsgs((prev) => ({ ...prev, [component.data.data.uuid]: errorMsg }))
        }
        continue
      }
    }

    // if the component is a Ruleset get all the unique labels names and clone them
    if (componentName === "Ruleset" && validatedComponents.length > 0) {
      const uniqueLabelNames = new Set<string>()

      validatedComponents.forEach((component) => {
        if (component && !isTableFormatFormikValues(component)) {
          component.cards.forEach((card) => {
            if (isRulesetFormikCard(card)) {
              uniqueLabelNames.add(card.label)
            }
          })
        }
      })

      await CreateLabelsMutation.mutateAsync({
        project_uuid: projectId,
        labelsNames: Array.from(uniqueLabelNames).map((name) => ({ name })),
      })
    }

    const uniqueDataFilesIds = new Set<string>()

    const clonedIds = Object.values(clonedFilesIdsMapper.current)

    try {
      // this loop to map all the ids of all data files to a single array
      for (const component of validatedComponents) {
        if (component && isTableFormatFormikValues(component)) {
          component.criteria_files.forEach((file) => {
            if (file.uuid && !clonedIds.includes(file.uuid))
              uniqueDataFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
          })
        } else {
          component?.cards?.forEach((card) => {
            if (card.condition_list_files.length > 0) {
              card.condition_list_files.forEach((file) => {
                if (file.uuid && !clonedIds.includes(file.uuid))
                  uniqueDataFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
              })
            }
          })
        }
      }

      // if there is data files send the request to clone all data files in one request
      // and when success map received files ides to their original ids in the object
      if (uniqueDataFilesIds.size > 0) {
        await cloneDataFilesMutation.mutateAsync({
          project_uuid: projectId,
          data_files: Array.from(uniqueDataFilesIds).map((file) => JSON.parse(file)),
        })

        validatedComponents.forEach((component) => {
          if (component) {
            allComponentsDataRef.current[component.uuid] = updateComponentsDataFilesIds(
              allComponentsDataRef.current[component.uuid] as ComponentTypeExtractor<
                Exclude<TVariant, "Scorecard">,
                "formikValues"
              >,
              clonedFilesIdsMapper.current,
            )
          }
        })
      }

      // create components promises array
      const requestPromises: { [compId: string]: Promise<AxiosResponse> } = {}

      // loop through all components and replace original file ids with new ones
      for (let i = 0; i < validatedComponents.length; i++) {
        const component = validatedComponents[i]

        if (!component) continue

        const componentFormikValues = allComponentsDataRef.current[component.uuid]

        if (!componentFormikValues) continue

        // Handle updating files for all Components except scoreTables
        if (!isTableFormatFormikValues(componentFormikValues)) {
          // will use formatcomponent when migrate all to new format
          const { trimmedName, featuresArray, finalComponentFormat } = formatWorkflowComponent({
            name: componentFormikValues.name,
            cards: componentFormikValues.cards as ScoreCardFormikValues["cards"],
          })

          // if user has no projects create a dummy project to add schema
          if (workflowsList?.length === 0 && featuresArray?.length > 0 && i === 0) {
            await createWorkflowMutation.mutateAsync({
              name: "Untitled-1",
              state: WorkflowCreateRequest.state.DISABLED,
              projectId: projectId,
              schema: [...featuresArray],
            })
          }

          // send each component request to create a copied component

          // Create a new component
          // we don't await here because we need all requests to be sent in one shot then
          // we can await on promise.allSettled
          requestPromises[component.uuid] = createComponentMutation.mutateAsync({
            projectId: projectId,
            name: trimmedName,
            rules: [...finalComponentFormat],
          })
        } else {
          const { adjustedCriteria, adjustedResults } = formatCriteria(
            componentFormikValues.criteria,
            componentFormikValues.results,
          )

          requestPromises[component.uuid] = createComponentMutation.mutateAsync({
            projectId: projectId,
            name: componentFormikValues.name.trim(),
            results: adjustedResults,
            features: componentFormikValues.features,
            format: ScoreCardCreateUpdateRequest.format.TABLE,
            criteria: adjustedCriteria,
          })
        }
      }

      const results = await Promise.allSettled(Object.values(requestPromises))

      // loop through all the promise results to remove fulfilled components
      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const compOriginalId = Object.keys(requestPromises)[i]

        if (!compOriginalId) continue

        if (result?.status === "fulfilled") {
          removeComponent?.(compOriginalId)
          setCompletedCardsCounter((prev) => ++prev)
        } else {
          const formattedErrorMessage =
            capitalize(result?.status === "rejected" && result?.reason?.response?.data?.name?.[0]) ||
            t("copyComponentFlow.audit.createLabelError", {
              componentName: t(`copyComponentFlow.componentNames.${componentName}`),
            })

          setComponentsErrorMsgs((prev) => ({
            ...prev,
            [compOriginalId]: formattedErrorMessage,
          }))
        }
      }

      NotificationUtils.toast(
        capitalize(
          t("copyComponentFlow.audit.saveAllHandlerSuccess", {
            projectName: currentEditProjectData?.[1].projName,
            componentName: t(`copyComponentFlow.componentNames.${componentName}`),
          }),
        ),
        {
          severity: "success",
        },
      )

      await queryClient.invalidateQueries(`${componentName}s`.toLowerCase())
      await queryClient.invalidateQueries(`${componentName}`.toLowerCase())
    } catch (err) {
      console.error(err)
    }
  }
  //#endregion

  //#region Variables
  const isPermittedToCloneFiles = useIsPermitted("Create Datafiles", permissions.konan, flattenedKonanPermissions)

  const isAllComponentsLoading =
    cloneDataFilesMutation.isLoading ||
    createWorkflowMutation.isLoading ||
    createComponentMutation.isLoading ||
    CreateLabelsMutation.isLoading

  const isOneComponentLoading = !!isWorkFlowMutating || !!isLabelsMutating || !!isCloneMutating || !!isCreateMutating

  const disableHeaderBtn =
    componentsDataResults.some((comp) => comp.status !== "success") || isAllComponentsLoading || isOneComponentLoading

  const isOneComponentHasDataFile = componentsDataResults.some((comp) => {
    const activeVersion = comp.data?.data.active_version
    return activeVersion?.rules?.some((rule) => rule.condition_list_files.length > 0)
  })

  //#endregion
  return (
    <Fragment>
      <div className={styles.headerContainer}>
        <div className={styles.progressContainer}>
          <Box className={styles.progressBar}>
            <CircularProgress
              variant="determinate"
              value={Math.floor((currentProjectStep / numOfTotalSelectedProjects) * 100)}
            />
            <Box className={styles.progressBarText}>
              <Typography variant="label-regular">
                {currentProjectStep} / {numOfTotalSelectedProjects}
              </Typography>
            </Box>
          </Box>

          <div>
            <Typography variant="h3-bold" gutterBottom>
              {currentEditProjectData?.[1].projName}
            </Typography>
            <Typography variant="label-regular" className={styles.itemProgText}>
              ({completedCardsCounter}/{totalNumOfCurrentProjComponents}){" "}
              {t(`copyComponentFlow.componentNames.${componentName}`)} {t("copyComponentFlow.audit.completed")}
            </Typography>
          </div>
        </div>

        <div className={styles.headerBtns}>
          <Button
            variant="secondary"
            disabled={disableHeaderBtn}
            onClick={() => incrementProjectStep()}
            tooltipPlacement="top"
            tooltip={t("copyComponentFlow.audit.dismissAllTooltip")}
          >
            {t("copyComponentFlow.audit.dimissAllBtn")}
          </Button>
          <Button
            variant="primary"
            disabled={disableHeaderBtn || (!isPermittedToCloneFiles && isOneComponentHasDataFile)}
            isLoading={isAllComponentsLoading || componentsDataResults.some((comp) => comp.status === "loading")}
            onClick={saveAllHandler}
          >
            {t("copyComponentFlow.audit.saveAllBtn")}
          </Button>
        </div>
      </div>

      <section className={`${styles.componentsSection} scrollbar`}>
        {componentsDataResults.map((component) => {
          const activeVersion = component.data?.data.active_version

          return (
            <div className={styles.itemContainer} key={uuidv4()}>
              {component.isFetching && (
                <div>
                  <Skeleton variant="rectangular" width={"100%"} marginBottom={16} />
                  <Skeleton variant="rectangular" width={"50%"} marginBottom={16} />
                </div>
              )}

              {component.status === "error" && (
                <KonanEmptyState
                  title={t("copyComponentFlow.audit.componentFetchingError")}
                  actionButtons={[
                    <Button variant="primary" onClick={component.refetch}>
                      {t("copyComponentFlow.audit.tryAgainBtn")}
                    </Button>,
                  ]}
                />
              )}

              {component.status === "success" && !component.isFetching && (
                <div>
                  {activeVersion?.rules?.some((rule) => rule.condition_list_files.length > 0) &&
                    !isPermittedToCloneFiles && (
                      <div className={styles.snackbar}>
                        <Snackbar severity={"info"} fullWidth>
                          {t("copyComponentFlow.audit.dontHavePermisionsToCopy", {
                            componentName: t(`copyComponentFlow.componentName.${componentName}`),
                          })}
                        </Snackbar>
                      </div>
                    )}

                  {componentsErrorMsgs?.[component.data.data.uuid] !== undefined && (
                    <div className={styles.snackbar}>
                      <Snackbar severity={"error"} fullWidth>
                        {componentsErrorMsgs[component.data.data.uuid]}
                      </Snackbar>
                    </div>
                  )}

                  {componentMapper<TVariant>(componentName, {
                    key: component.data.data.uuid,
                    variant: "import",
                    uuid: component.data.data.uuid,
                    isAllComponentsLoading,
                    removeComponent,
                    setTotalNumOfCurrentProjComponents,
                    setCompletedCardsCounter,
                    setComponentsErrorMsgs,
                    isPermittedToCloneFiles,
                    createdBy: activeVersion?.created_by ?? "",
                    createdAt: activeVersion?.created_at ?? "",
                    allComponentsDataRef,
                    canShowDetails: canShowDetails,
                    clonedFilesIdsMap: clonedFilesIdsMapper.current,
                  })}
                </div>
              )}
            </div>
          )
        })}
      </section>
    </Fragment>
  )
}
